import { useRef, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, MessageCircle, User } from 'lucide-react'
import ReactMarkdown, { type Options as ReactMarkdownOptions } from 'react-markdown'
import type { Comment } from '@/types/comment'
import { useAppStore } from '@/store'
import TooltipOverlay from '@/components/TooltipOverlay'
import { colors } from '@/styles/colors'
import { cn } from '@/lib/utils'
import CommentActions from './CommentActions'
import ReactionBar from './ReactionBar'
import ReactionPicker from './ReactionPicker'
import {
  getUserAvatar,
  getUserAvatarInitial,
  getUserDisplayName,
  USER_AVATAR_GRADIENT_CLASS,
} from '@/utils/userUtils'
import { useOrganizationDirectory } from '@/hooks/useOrganizationDirectory'
import {
  mergeMarkdownComponents,
  remarkPlugins,
  rehypePlugins,
} from '@/config/markdownConfig'

// react-markdown's remark/rehype plugin props expect `PluggableList`. The
// shared `remarkPlugins`/`rehypePlugins` arrays in markdownConfig are inferred
// with a wider literal type because of their `[plugin, options]` tuples, so we
// re-assert them to react-markdown's own plugin-list type at the call site.
type MarkdownPluginList = NonNullable<ReactMarkdownOptions['remarkPlugins']>

type CommentBubbleProps = {
  comment: Comment
  workspaceId: string
  isOwnComment: boolean
  onReply: (commentId: string) => void
  onEdit: (commentId: string) => void
  isUnread?: boolean
}

const TIME_FORMAT = { hour: '2-digit', minute: '2-digit' } as const
const EDIT_COMMENT = 'Edit comment'

const CommentBubble = ({
  comment,
  workspaceId,
  isOwnComment,
  onReply,
  onEdit,
  isUnread = false,
}: CommentBubbleProps) => {
  const { user, toggleReaction } = useAppStore()
  const containerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const date = new Date(comment.created_at).toLocaleTimeString([], TIME_FORMAT)

  const handleReactionSelect = async (emoji: string) => {
    if (!user) return
    try {
      await toggleReaction(workspaceId, comment.id, emoji, user.userId)
    } catch (error) {
      console.error('Failed to add reaction:', error)
    }
  }

  const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' })

  useEffect(() => {
    scrollToBottom()
  }, [])

  // Anyone who is not the signed-in user resolves through the org directory —
  // the store knows only `user`, so without it an author is a bare UUID.
  const directory = useOrganizationDirectory()

  // Get display name for the comment author
  const displayName = getUserDisplayName(comment.user_id, user, directory)

  // Avatar for the comment author. `getUserAvatar` only resolves a photo for
  // the current user (the store holds no directory of other users' pictures),
  // so every other author falls through to the initial on the gradient.
  const [avatarFailed, setAvatarFailed] = useState(false)
  const avatarSource = getUserAvatar(comment.user_id, user, directory)

  // Clear a past failure whenever the photo's source changes, so the bubble
  // retries instead of latching the initial. Without this a single transient
  // 404 sticks for as long as the bubble stays mounted — and a comment panel
  // stays open a long time, so an avatar uploaded in settings would never
  // appear. Keyed on `user` as well as the URL because authSessionSlice
  // rebuilds `user` on every auth refresh, which is exactly the case where the
  // SAME url deserves another attempt. Same guard as UserMenu.
  useEffect(() => {
    setAvatarFailed(false)
  }, [avatarSource, user])

  const avatarUrl = avatarFailed ? null : avatarSource
  // `null` when the name is unknown — paint a glyph, never a UUID character.
  const initial = getUserAvatarInitial(comment.user_id, user, directory)

  // Highlight mentions in content
  const highlightMentions = (content: string) => {
    const mentionRegex = /@[\w\s]+/g
    const result = content.replace(mentionRegex, (match) => {
      const trimmedMatch = match.trim()
      return `**${trimmedMatch}**`
    })
    return result
  }

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative group"
    >
      <div className="flex items-start gap-3">
        {/* User Avatar — the one app-wide avatar gradient (FS-581), matching
            the profile bubble in the left rail and the thread AvatarStack.
            This used to branch on `isOwnComment` into a success/gray circle
            wrapping ProfilePicture, so the SAME user rendered green-on-gray
            here and cyan-on-gradient in the rail. */}
        <div className="flex-shrink-0">
          <div
            className={cn(
              'flex size-8 items-center justify-center overflow-hidden rounded-full text-xs font-semibold text-white',
              USER_AVATAR_GRADIENT_CLASS
            )}
          >
            {avatarUrl ? (
              // The gradient stays underneath as the backdrop while the photo
              // loads, and an expired/rotated IdP URL falls back to the initial
              // rather than leaving a broken image (same treatment as UserMenu).
              <img
                src={avatarUrl}
                alt=""
                className="size-full object-cover"
                referrerPolicy="no-referrer"
                draggable={false}
                onError={() => setAvatarFailed(true)}
              />
            ) : initial ? (
              initial
            ) : (
              <User className="size-4" aria-hidden />
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          {/* Header with name, timestamp, and unread indicator */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-900 dark:text-dark-text">
              {displayName}
            </span>
            <span className="text-xs text-gray-500 dark:text-dark-text-muted">
              {date}
            </span>
            {isUnread && (
              <TooltipOverlay message="Unread">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: colors.blue[500] }}
                />
              </TooltipOverlay>
            )}
            {comment.is_resolved && (
              <TooltipOverlay message="Resolved">
                <CheckCircle2
                  size={14}
                  className="text-gray-400 dark:text-gray-500"
                />
              </TooltipOverlay>
            )}
          </div>

          {/* Message Content */}
          <div className="text-sm text-gray-900 dark:text-dark-text">
            <ReactMarkdown
              components={mergeMarkdownComponents()}
              remarkPlugins={remarkPlugins as MarkdownPluginList}
              rehypePlugins={rehypePlugins as MarkdownPluginList}
            >
              {highlightMentions(comment.content)}
            </ReactMarkdown>
          </div>

          {/* Reactions - Inline with message */}
          {comment.reactions.length > 0 && (
            <div className="mt-1">
              <ReactionBar
                commentId={comment.id}
                workspaceId={workspaceId}
                reactions={comment.reactions}
              />
            </div>
          )}

          {/* Actions - Show on hover or if there are replies */}
          <div className="flex items-center gap-3 mt-2">
            {!isOwnComment && (
              <CommentActions
                comment={comment}
                onReply={onReply}
              />
            )}
            {isOwnComment && (
              <TooltipOverlay message={EDIT_COMMENT}>
                <button
                  className="text-xs font-medium transition-opacity opacity-60 hover:opacity-100 text-gray-600 dark:text-gray-400"
                  onClick={() => onEdit(comment.id)}
                >
                  Edit
                </button>
              </TooltipOverlay>
            )}
            {comment.reply_count > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-dark-text-muted">
                <MessageCircle size={12} />
                <span>{comment.reply_count} {comment.reply_count === 1 ? 'reply' : 'replies'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Hover Actions - Add reaction button */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15 }}
              className="flex-shrink-0"
            >
              <ReactionPicker
                onReactionSelect={handleReactionSelect}
                disabled={!user}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div ref={bottomRef} />
    </motion.div>
  )
}

export default CommentBubble
